import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import React from 'react';

const LOGO_MAP: [string, string][] = [
    ['ftmo', '/static/img/finance/ftmo.png'],
    ['trading pit', '/static/img/finance/the_trading_pit.jpg'],
    ['the5ers', '/static/img/finance/the5ers.png'],
    ['apex', '/static/img/finance/apex_fund.png'],
    ['topstep', '/static/img/finance/topstep.jpeg'],
    ['darwinex', '/static/img/finance/darwinex.jpg'],
    ['zimtra', '/static/img/finance/zimtra.jpeg'],
    ['axiselect', '/static/img/finance/axiselect.jpeg'],
    ['axia', '/static/img/finance/axiselect.jpeg'],
    ['bitget', '/static/img/finance/bitget.png'],
];

const getFirmLogo = (name: string): string | null => {
    const lower = name.toLowerCase();
    const match = LOGO_MAP.find(([keyword]) => lower.includes(keyword));
    return match ? match[1] : null;
};

interface FirmItem {
    name: string;
    features: string[];
    link_url: string;
    link_text?: string;
    highlight?: boolean;
}

interface Section {
    title: string;
    items: FirmItem[];
}

interface GuideSection {
    title: string;
    button_text: string;
    button_url: string;
}

interface PageConfig {
    title: string;
    sections: Section[];
    guide?: GuideSection;
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
        config: PageConfig;
    };
}

const FirmCard: React.FC<{ firm: FirmItem }> = ({ firm }) => {
    const logo = getFirmLogo(firm.name);
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:flex">
            {/* Logo panel */}
            <div className="flex w-full items-center justify-center border-b border-gray-100 p-8 md:w-52 md:flex-shrink-0 md:border-b-0 md:border-r">
                {logo ? (
                    <img
                        src={logo}
                        alt={firm.name}
                        className="max-h-24 w-full object-contain"
                    />
                ) : (
                    <span className="text-lg font-bold text-gray-900">{firm.name}</span>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900">{firm.name}</h3>
                    <ul className="space-y-1.5">
                        {firm.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-base text-gray-700">
                                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {firm.link_url && (
                    <div className="mt-5">
                        <a
                            href={firm.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="primary-button inline-flex w-full items-center justify-center gap-2"
                        >
                            <ExternalLink className="h-4 w-4" />
                            {firm.link_text ?? 'ACCEDE AQUÍ'}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

const FirmasPropietariasContent: React.FC = () => {
    const { static_page } = usePage<{ static_page: PageProps['static_page'] }>().props;
    const config = static_page.config;

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="mx-auto max-w-5xl space-y-6">
                <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                    {config.title}
                </h1>

                {config.sections.map((section, i) => (
                    <div key={i} className="space-y-3">
                        <h2 className="text-3xl font-bold text-gray-900">
                            {section.title}
                        </h2>
                        <div className="space-y-3">
                            {section.items.map((firm, j) => (
                                <React.Fragment key={j}>
                                    <FirmCard firm={firm} />
                                    {(config.guide && firm.name.toLowerCase().includes('trading pit')) ? (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
                                            <h2 className="mb-3 text-xl font-semibold text-gray-900">
                                                {config.guide.title}
                                            </h2>
                                            <a
                                                href={config.guide.button_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="primary-button inline-flex items-center gap-2"
                                            >
                                                {config.guide.button_text}
                                            </a>
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </AppLayout>
    );
};

const FirmasPropietarias: React.FC = () => (
    <ThemeWrapper>
        <FirmasPropietariasContent />
    </ThemeWrapper>
);

export default FirmasPropietarias;
